import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export type UserRole = 'admin' | 'moderator' | 'user';

export const useUserRole = () => {
  const { user, isAuthenticated } = useAuth();
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const fetchUserRoles = async () => {
      if (!isAuthenticated || !user?.id) {
        setRoles([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const { data, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id);

        if (error) {
          console.error('Error fetching user roles:', error);
          setError(error.message);
          setRoles([]);
        } else if (isMounted) {
          const userRoles = data?.map(r => r.role as UserRole) || [];
          setRoles(userRoles);
        }
      } catch (err) {
        console.error('Unexpected error fetching roles:', err);
        setError('Failed to fetch user roles');
        setRoles([]);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchUserRoles();

    return () => {
      isMounted = false;
    };
  }, [user?.id, isAuthenticated]);

  const hasRole = (role: UserRole): boolean => {
    return roles.includes(role);
  };

  const isAdmin = hasRole('admin');
  const isModerator = hasRole('moderator');
  const isUser = hasRole('user');

  return {
    roles,
    hasRole,
    isAdmin,
    isModerator,
    isUser,
    loading,
    error
  };
};